import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import clone from 'ramda/src/clone';
import filter from 'ramda/src/filter';
import isNil from 'ramda/src/isNil';
import uniq from 'ramda/src/uniq';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import Accordion from '@components/@core/Accordion';
import Button from '@components/@core/AnimatedButton';
import Dropdown from '@components/@core/Dropdown';
import Filter from '@components/@core/Filter';
import Icon from '@components/@core/Icon';
import Input from '@components/@core/Input';
import Thumbnail from '@components/@core/Thumbnail';
import Toggle from '@components/@core/Toggle';
import Upload from '@components/@core/Upload';
import Pagination from '@components/@core/Pagination';
import AppActions from '~/actions/app';
import { LearningModule, TimeIntervals, InitQuiz } from '~/services/config';
import { blobToBase64 } from '~/services/util';
import Question from './Question';

import QuillEditor from '../@core/QuillEditor';

const Add = ({ onUpdate }) => {
    const [type, setType] = useState(LearningModule[3]);
    const [nameEn, setNameEn] = useState('');
    const [descriptionEn, setDescriptionEn] = useState('');
    const [nameEs, setNameEs] = useState('');
    const [descriptionEs, setDescriptionEs] = useState('');
    const [thumbnail, setThumbnail] = useState('');
    const [video, setVideo] = useState('');
    const [presentationFile, setPresentationFile] = useState('');
    const [file, setFile] = useState('');
    const [linkEn, setLinkEn] = useState('');
    const [linkEs, setLinkEs] = useState('');
    const [videoURL, setVideoURL] = useState('');
    const [composer, setComposer] = useState('');
    const [objectives, setObjectives] = useState(['']);
    const [points, setPoints] = useState(null);
    const [selected, setSelected] = useState({
        author: [],
        department: [],
        competency: [],
        category: [],
    });
    const [approval, setApproval] = useState(false);
    const [chronologicalLock, setChronologicalLock] = useState(false);
    const [timeLock, setTimeLock] = useState(false);
    const [page, setPage] = useState(1);
    const [per, setPer] = useState(5);
    const [questions, setQuestions] = useState([InitQuiz[1], InitQuiz[2], InitQuiz[4]]);
    const [companyId, setCompanyId] = useState(0);
    const [userId, setUserId] = useState(0);
    const [modalOpen, setModalOpen] = useState(false);

    const dispatch = useDispatch();

    const modules = {
        toolbar: [['bold', 'italic', 'underline'], ['link'], [{ list: 'ordered' }, { list: 'bullet' }], ['clean']],
    };

    const handleAddAnswer = (e) => {
        const _questions = clone(questions);
        _questions[e - 1].rules.options.push('');
        setQuestions(_questions);
    };

    const handleRemoveAnswer = (type, qIndex, index) => {
        if (questions[qIndex - 1].rules.options.length === 1) {
            toast.error('Quiz should have one more answers.', {
                position: toast.POSITION.TOP_RIGHT,
                pauseOnFocusLoss: false,
                hideProgressBar: true,
            });
            return;
        }
        const _questions = clone(questions);
        const { answer } = _questions[qIndex - 1].rules;

        if (type === 3) {
            if (!isNil(answer)) {
                _questions[qIndex - 1].rules.answer = Number(answer) === index + 1 ? '' : Number(answer) > index + 1 ? String(Number(answer) - 1) : answer;
            }
        }
        if (type === 4) {
            _questions[qIndex - 1].rules.answer = answer.filter((x) => Number(x) !== index + 1).map((y) => (Number(y) > index + 1 ? String(Number(y) - 1) : y));
        }
        _questions[qIndex - 1].rules.options.splice(index, 1);
        setQuestions(_questions);
    };

    const handleAddQuestion = () => {
        const _questions = clone(questions);
        _questions.push(InitQuiz[1]);
        setQuestions(_questions);
    };

    const handleChangeQuizType = (e, idx) => {
        const _questions = clone(questions);
        const prevQuestion = questions[idx];

        if (e[0] === prevQuestion.type) return;

        let _rules;

        if (e[0] === 3 || e[0] === 4) {
            if (prevQuestion.type === 3) {
                _rules = {
                    answer: [prevQuestion.rules.answer],
                    options: prevQuestion.rules.options,
                };
            } else if (prevQuestion.type === 4) {
                _rules = {
                    answer: prevQuestion.rules.answer.length > 0 ? prevQuestion.rules.answer[0] : '',
                    options: prevQuestion.rules.options,
                };
            } else {
                _rules = {
                    answer: e[0] === 3 ? '' : [],
                    options: [prevQuestion.rules.answer, ''],
                };
            }
        } else {
            if (prevQuestion.type === 3 || prevQuestion.type === 4) {
                _rules = {
                    answer: prevQuestion.rules.options[0],
                };
            } else {
                _rules = {
                    answer: prevQuestion.rules.answer,
                };
            }
        }

        _questions[idx] = {
            ...InitQuiz[e[0]],
            wording: prevQuestion.wording,
            rules: {
                ...InitQuiz[e[0]].rules,
                ..._rules,
            },
        };

        setQuestions(_questions);
    };

    const handleChangeQuestion = (e, idx) => {
        const _questions = clone(questions);
        _questions[idx].wording = e;
        setQuestions(_questions);
    };

    const handleChangeAnswers = (data, idx1, idx2) => {
        const _questions = clone(questions);
        if (questions[idx1].type === 3 || questions[idx1].type === 4) _questions[idx1].rules.options[idx2] = data;
        else _questions[idx1].rules.answer = data;
        setQuestions(_questions);
    };

    const handleChangeChecked = (e, idx1, idx2) => {
        const _questions = clone(questions);
        const index = `${idx2}`;
        if (questions[idx1].type === 3) {
            _questions[idx1].rules.answer = e ? index : '';
        } else {
            let { answer } = _questions[idx1].rules;
            if (e) answer.push(index);
            else answer = filter((x) => x != index, answer);
            _questions[idx1].rules.answer = uniq(answer);
        }
        setQuestions(_questions);
    };

    const handleRemoveQuestion = (idx) => {
        if (questions.length === 1) {
            toast.error('Quiz module should have one more questions.', {
                position: toast.POSITION.TOP_RIGHT,
                pauseOnFocusLoss: false,
                hideProgressBar: true,
            });
            return;
        }
        const _questions = clone(questions);
        _questions.splice(idx, 1);
        if (_questions.length % 5 === 0) setPage((page) => page - 1);
        setQuestions(_questions);
    };

    const handlePresentationFile = (e) => {
        setPresentationFile(e[0]);
        if (!presentationFile) {
            dispatch(
                AppActions.modalRequest({
                    type: 'Confirm',
                    data: {
                        before: {
                            title: 'Converting',
                            body: 'The presentation file is converting to be browser compatible, please wait a few minutes before previewing the module',
                            yesButtonText: 'OK',
                            noButtonText: null,
                        },
                    },
                    callBack: {
                        onYes: () => {},
                    },
                })
            );
        }
    };

    const handleFile = (e) => {
        e.length > 1 ? setFile(e) : setFile(e[0]);
    };

    const handleObjectiveChange = (index) => (e) => {
        const _objectives = clone(objectives);
        _objectives[index] = e;
        setObjectives(_objectives);
    };

    const handleObjectiveAdd = () => {
        const _objectives = clone(objectives);
        if (_objectives.length <= '4') {
            _objectives.push('');
            setObjectives(_objectives);
        }
    };

    const handleObjectiveDelete = (index) => () => {
        const _objectives = clone(objectives);
        _objectives.splice(index, 1);
        setObjectives(_objectives);
    };

    const handlePreview = () => {
        const _file = file.map((file) => {
            return file.name;
        });
        const item = {
            id: null,
            card_type_id: type.id,
            data: {
                en: { name: nameEn, description: descriptionEn, link: linkEn },
                es: { name: nameEs, description: descriptionEs, link: linkEs },
                tags: [],
                thumb_url: thumbnail,
                video_url: videoURL,
                competency: [],
                completion_points: points,
                curriculum: [],
                department: [],
                attachments: _file,
                objectives,
            },
            recommended: null,
            published: 0,
            children: [],
        };
        const payload = {
            type: 'Preview',
            data: { before: { course: { children: [item] }, module: item, index: 0 } },
        };
        if (!modalOpen && type.label === 'Presentation') {
            dispatch(
                AppActions.modalRequest({
                    type: 'Confirm',
                    data: {
                        before: {
                            title: 'Converting',
                            body: 'The presentation file is converting to be browser compatible, please wait a few minutes before previewing the module',
                            yesButtonText: 'OK',
                            noButtonText: null,
                        },
                    },
                    callBack: {
                        onYes: () => {
                            setModalOpen(true);
                        },
                    },
                })
            );
        } else {
            dispatch(AppActions.modalRequest(payload));
        }
    };

    const handleSelectTags = (key) => (tags) => {
        const _selected = clone(selected);
        _selected[key] = tags;
        setSelected(_selected);
    };

    const handleFilter = (type, e) => {
        if (type === 'company') setCompanyId(e[0].id);
        else setUserId(e[0].id);
    };

    const extractHTMLContent = (string) => {
        const span = document.createElement('span');
        span.innerHTML = string;
        return span.textContent || span.innerText;
    };

    const handleSubmit = (published) => async () => {
        let payload = {
            template: {
                card_type_id: type.id,
                author_id: selected.author[0],
                access_type: 1,
                published,
                data: {
                    en: { name: nameEn, description: descriptionEn, link: linkEn },
                    es: { name: nameEs, description: descriptionEs, link: linkEs },
                    category: selected.category,
                    competency: selected.competency,
                    department: selected.department,
                    objectives,
                    completion_points: points,
                    thumb_url: '',
                    attachment_hash: '',
                    attachments: [],
                    estimated_completion: TimeIntervals[5].value,
                    action_approval: 'self',
                    require_attachments: '',
                    assessment_id: null,
                    presentation_url: '',
                    study_content: '',
                    video_url: videoURL,
                    embed_url: '',
                },
            },
        };

        if (type.label === 'Scorm') {
            if (file === '' && linkEn === '' && linkEs === '') {
                toast.error('Zip is required', {
                    position: toast.POSITION.TOP_RIGHT,
                    pauseOnFocusLoss: false,
                    hideProgressBar: true,
                });
                return;
            } else if (file !== '') {
                payload.scorm_zip = (await blobToBase64(file)).split(',').pop();
                return onUpdate(payload, [thumbnail, video]);
            }
        }

        if (type.label === 'Quiz') {
            let ques = clone(questions);
            for (const question of ques) {
                const { type, rules, wording } = question;
                if (wording === '') {
                    toast.error('Empty question!', {
                        position: toast.POSITION.TOP_RIGHT,
                        pauseOnFocusLoss: false,
                        hideProgressBar: true,
                    });
                    return;
                }
                if (type === 3 || type === 4) {
                    const opt = rules.options.filter((e) => e);
                    if (opt.length == 0 || opt == '') {
                        toast.error('Missing answer!', {
                            position: toast.POSITION.TOP_RIGHT,
                            pauseOnFocusLoss: false,
                            hideProgressBar: true,
                        });
                        return;
                    }
                    const options = Object.assign(...opt.map((x, index) => ({ [index + 1]: x })));
                    question.rules.options = options;
                } else {
                    if (rules.answer == '') {
                        toast.error('Missing answer!', {
                            position: toast.POSITION.TOP_RIGHT,
                            pauseOnFocusLoss: false,
                            hideProgressBar: true,
                        });
                        return;
                    }
                }
            }
            if (ques.length == 0) {
                toast.error('Invalid questions!', {
                    position: toast.POSITION.TOP_RIGHT,
                    pauseOnFocusLoss: false,
                    hideProgressBar: true,
                });
                return;
            }
            const items = Object.assign(...ques.map((x, index) => ({ [index + 1]: x })));
            payload = {
                assessment: {
                    company_id: companyId,
                    user_id: null,
                    title: nameEn,
                    type: 2,
                    status: 0,
                    author_id: selected.author[0],
                    data: {
                        quiz: {
                            items,
                            rules: { taken: `${questions.length}`, correct: `${questions.length}` },
                        },
                        settings: { order: 'linear', show_correct: 'yes' },
                        en: { name: nameEn, description: descriptionEn },
                        es: { name: nameEs, description: descriptionEs },
                        category: selected.category,
                        competency: selected.competency,
                        department: selected.department,
                        objectives,
                        assessment_id: null,
                    },
                    result: null,
                    deleted_at: null,
                    published,
                },
                data: {
                    card_type_id: 5,
                    data: {
                        en: { name: nameEn, description: descriptionEn, link: '' },
                        es: { name: nameEs, description: descriptionEs, link: '' },
                        tags: [],
                        type: 'recurring',
                        category: selected.category,
                        thumb_url: '',
                        video_url: '',
                        competency: selected.competency,
                        delay_days: 0,
                        department: selected.department,
                        attachments: [],
                        combo_order: '',
                        assessment_id: null,
                        meta_category: '',
                        action_approval: approval ? 'manager' : 'self',
                        assessment_type: '',
                    },
                    author_id: selected.author[0],
                    parent_id: null,
                    blocked_by: null,
                    quota: '0',
                    access_type: 1,
                    rating: null,
                    recommended: null,
                    published,
                },
            };
        }

        onUpdate(payload, [thumbnail, video, presentationFile, file]);
    };

    const authors = useSelector((state) => state.app.authors);
    const departments = useSelector((state) => state.app.departments);
    const competencies = useSelector((state) => state.app.competencies);
    const categories = useSelector((state) => state.app.categories);
    const showModuleComposer = !['Presentation', 'Quiz', 'Video'].includes(type.label);
    const showAttachment = !['Quiz', 'Scorm'].includes(type.label);
    const disabled = !nameEn || selected.author.length === 0 || (type.label === 'Quiz' ? false : !descriptionEn);
    const disableDraft = !nameEn || !descriptionEn || !nameEs || !descriptionEs;

    return (
        <>
            <Filter back="all modules" onChange={handleFilter} />
            <div className="library-module-card border-5 mb-2 pt-3" data-cy="library-training-addModule">
                <div className="detail-content">
                    <div className="d-flex jc-between mb-3">
                        <span className="dsl-b22 bold">Create Module</span>
                    </div>
                    <p className="dsl-m12 mb-2">Module type</p>
                    <div className="d-flex ai-center my-3">
                        <Dropdown data={LearningModule} defaultIndexes={[3]} dataCy="library-training-module-type" returnBy="data" getValue={(item) => item['label']} onChange={(e) => setType(e[0])} />
                    </div>

                    <div className="d-flex ssm-column mt-3">
                        <div>
                            <p className="dsl-m12 mb-1">Module image</p>
                            <Thumbnail source={thumbnail} type="upload" size="medium" label="Upload image" isDisablePicker onDrop={(e) => setThumbnail(e)} onDelete={() => setThumbnail(null)} />
                        </div>
                        <div className="d-flex flex-column ps-4 ssp-l-0 mt-0 ssm-t-2 d-flex-1 module-description">
                            <Input
                                className="module-input mb-3"
                                title="Module title"
                                dataCy="library-training-module-title"
                                placeholder="Type here..."
                                value={nameEn}
                                direction="vertical"
                                onChange={(e) => setNameEn(e)}
                            />
                            <span className="title-font">Module description</span>
                            <div className="outer-quill">
                                <QuillEditor
                                    className="module-text d-flex-1"
                                    title="Module description"
                                    dataCy="library-training-module-description"
                                    placeholder="Type here..."
                                    value={descriptionEn}
                                    theme="snow"
                                    modules={{ toolbar: { container: [['link']] } }}
                                    onChange={(e) => setDescriptionEn(e)}
                                />
                                {extractHTMLContent(descriptionEn) && focus && (
                                    <div className="clear" onClick={() => setDescriptionEn('')}>
                                        <Icon name="fas fa-xmark-circle cursor-pointer" color="#969faa" size={16} />
                                    </div>
                                )}
                            </div>
                            <Input
                                className="module-input mb-3 mt-3"
                                title="Module title (Spanish)"
                                dataCy="library-training-module-title-es"
                                placeholder="Type here..."
                                value={nameEs}
                                direction="vertical"
                                onChange={(e) => setNameEs(e)}
                            />
                            <span className="title-font">Module description (Spanish)</span>
                            <div className="outer-quill">
                                <QuillEditor
                                    className="module-text d-flex-1"
                                    title="Module description (Spanish)"
                                    dataCy="library-training-module-description-es"
                                    placeholder="Type here..."
                                    value={descriptionEs}
                                    theme="snow"
                                    modules={{ toolbar: { container: [['link']] } }}
                                    onChange={(e) => setDescriptionEs(e)}
                                />
                                {extractHTMLContent(descriptionEs) && focus && (
                                    <div className="clear" onClick={() => setDescriptionEs('')}>
                                        <Icon name="fas fa-xmark-circle cursor-pointer" color="#969faa" size={16} />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {type.label === 'Video' && (
                        <div className="d-flex ssm-column mt-3 me-0 ssm-r-2">
                            <div className="d-flex-1 me-0 ssm-r-5 pe-0 ssp-r-5">
                                <div className="mb-0 d-flex">
                                    <p class="dsl-m12 mb-0">File</p>
                                    {!video && <p className="dsl-l10 mb-0 ms-1">(File types supported AVI, MP4)</p>}
                                </div>
                                <Upload type="low" accept="video/*" title="Upload video file" icon="fas fa-file-upload" color="#376caf" size={16} onDrop={(e) => setVideo(e[0])} />
                            </div>
                            <div className="d-flex-1 mt-sm-3">
                                <p className="dsl-m12 mb-1">Or place the video link</p>
                                <Input
                                    className="video-url"
                                    dataCy="library-training-module-videoUrl"
                                    placeholder="Type here..."
                                    direction="vertical"
                                    value={videoURL}
                                    onChange={(e) => setVideoURL(e)}
                                />
                            </div>
                        </div>
                    )}

                    {showModuleComposer && (
                        <div className="my-4 module-composer">
                            <p className="dsl-m12 my-1">Module composer:</p>
                            <QuillEditor value={composer} modules={modules} onChange={(e) => setComposer(e)} />
                        </div>
                    )}

                    <div className="d-flex ai-center">
                        {showAttachment && (
                            <div className="d-flex ssm-column ai-center mt-3 me-0 ssm-r-2">
                                <div className="d-flex-1 me-0 ssm-r-5 pe-0 ssp-r-5">
                                    {type.label === 'Presentation' ? (
                                        <>
                                            <div className="mb-0 d-flex">
                                                <p className="dsl-m12 mb-1">File</p>
                                                {!presentationFile && <p className="dsl-l10 mb-0 ms-1">(File types supported PPT, PDF, DOC, XLS)</p>}
                                            </div>
                                            <div className="d-flex mt-2">
                                                <Upload
                                                    title="Upload presentation file"
                                                    icon="fas fa-file-upload"
                                                    color="#376caf"
                                                    accept=".ppt, .pptx, .pot, .potx, .odp, .pdf, .xls, .xlsx, .doc, .docx"
                                                    size={16}
                                                    onDrop={handlePresentationFile}
                                                />
                                            </div>
                                            <Upload title="Upload file" icon="fas fa-file-upload" color="#376caf" size={16} multiple onDrop={handleFile} />
                                        </>
                                    ) : (
                                        <>
                                            <div className="mb-0 d-flex">
                                                <p className="dsl-m12 mb-1">File</p>
                                                {!file && <p className="dsl-l10 mb-0 ms-1">(File types supported PPT, PDF, DOC, XLS)</p>}
                                            </div>
                                            <div className="d-flex">
                                                <Upload
                                                    title="Upload file"
                                                    icon="fas fa-file-upload"
                                                    color="#376caf"
                                                    accept=".ppt, .pptx, .pot, .potx, .odp, .pdf, .xls, .xlsx, .doc, .docx"
                                                    multiple
                                                    size={16}
                                                    onDrop={handleFile}
                                                />
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                    {type.label === 'Scorm' && (
                        <>
                            <div className="mt-3">
                                <div className="d-flex ssm-column mt-3 me-0 ssm-r-2">
                                    <div className="d-flex-1 me-0 ssm-r-5 pe-0 ssp-r-5">
                                        <div className="mb-0 d-flex">
                                            <p className="dsl-m12 mb-1">File</p>
                                            {!file && <p className="dsl-l10 mb-0 ms-1">(File types supported ZIP)</p>}
                                        </div>
                                        <Upload title="Upload ZIP file" icon="fas fa-file-upload" color="#376caf" accept=".zip" size={16} onDrop={handleFile} />
                                    </div>
                                </div>
                                <Input
                                    className="video-url mt-3"
                                    placeholder="Paste English scorm link..."
                                    dataCy="library-training-module-scormLink"
                                    title="Scorm link (English)"
                                    direction="vertical"
                                    value={linkEn}
                                    onChange={(e) => setLinkEn(e)}
                                />
                                <Input
                                    className="video-url mt-3"
                                    placeholder="Paste Spanish scorm link..."
                                    dataCy="library-training-module-scormLink-es"
                                    title="Scorm link (Spanish)"
                                    direction="vertical"
                                    value={linkEs}
                                    onChange={(e) => setLinkEs(e)}
                                />
                            </div>
                        </>
                    )}
                    <p className="dsl-m12 mt-4">Learning objectives:</p>
                    {objectives.map((item, index) => (
                        <div className="d-flex" key={`ob${index}`}>
                            <Input
                                className="module-input mb-2 d-flex-1"
                                dataCy={`library-training-module-learningObjectiveContent${index}`}
                                key={`objectives${index}`}
                                value={item}
                                placeholder="Type here..."
                                direction="vertical"
                                onChange={handleObjectiveChange(index)}
                            />
                            <Button className="ml-auto" dataCy={`library-training-module-learningObjectiveTrashBtn${index}`} type="link" onClick={handleObjectiveDelete(index)}>
                                <Icon name="fas fa-trash-can" size={14} color="#376caf" />
                            </Button>
                        </div>
                    ))}
                    <Button className="ml-auto" dataCy="library-training-module-learningObjectiveAddBtn" type="link" onClick={handleObjectiveAdd}>
                        <Icon name="fas fa-plus" size={14} color="#376caf" />
                        <span className="dsl-p14 text-400 ms-1">ADD OBJECTIVE</span>
                    </Button>
                    <Input
                        className="module-input mt-3"
                        dataCy="library-training-module-completion_points"
                        type="number"
                        placeholder="Enter points..."
                        title="Completion Points"
                        direction="vertical"
                        value={points}
                        onChange={(e) => setPoints(parseInt(e) || null)}
                    />
                    <Accordion className="settings" title="Filter Settings" icon="" expanded>
                        <Row className="mt-1">
                            <Col xs={12} sm={6} className="my-3">
                                <Dropdown
                                    className="dropdown-field"
                                    title="Author"
                                    dataCy="library-training-module-author"
                                    width="fit-content"
                                    data={authors}
                                    direction="vertical"
                                    getValue={(e) => e.name}
                                    onChange={handleSelectTags('author')}
                                />
                            </Col>
                            <Col xs={12} sm={6} className="my-3">
                                <Dropdown
                                    multi
                                    className="dropdown-field"
                                    title="Categories"
                                    dataCy="library-training-module-categories"
                                    width="fit-content"
                                    data={categories}
                                    direction="vertical"
                                    placeholder="Select multiple"
                                    getValue={(e) => e.name}
                                    onChange={handleSelectTags('category')}
                                />
                            </Col>
                            <Col xs={12} sm={6} className="my-3">
                                <Dropdown
                                    multi
                                    className="dropdown-field"
                                    title="Competencies"
                                    dataCy="library-training-module-competencies"
                                    width="fit-content"
                                    placeholder="Select multiple"
                                    data={competencies}
                                    direction="vertical"
                                    getValue={(e) => e.name}
                                    onChange={handleSelectTags('competency')}
                                />
                            </Col>
                            <Col xs={12} sm={6} className="my-3">
                                <Dropdown
                                    multi
                                    className="dropdown-field"
                                    title="Departments"
                                    dataCy="library-training-module-department"
                                    width="fit-content"
                                    data={departments}
                                    direction="vertical"
                                    placeholder="Select multiple"
                                    getValue={(e) => e.name}
                                    onChange={handleSelectTags('department')}
                                />
                            </Col>
                            <Col xs={12} sm={6} className="my-3">
                                <Dropdown
                                    className="dropdown-field"
                                    title="Time to complete"
                                    data={TimeIntervals}
                                    dataCy="library-training-module-timeInterval"
                                    defaultIndexes={[0]}
                                    direction="vertical"
                                    width="fit-content"
                                    getValue={(item) => item['label']}
                                />
                            </Col>
                            <Col xs={12} sm={6} className="my-3">
                                <Toggle
                                    className="toggle-button"
                                    checked={approval}
                                    title="Manager approval"
                                    dataCy="library-training-module-managerApproval"
                                    leftLabel="Off"
                                    rightLabel="On"
                                    direction="vertical"
                                    onChange={(e) => setApproval(e)}
                                />
                            </Col>

                            {type.label !== 'Quiz' && (
                                <>
                                    <Col xs={12} sm={6} className="my-3">
                                        <Input disabled className="input-field" title="Est days to complete" direction="vertical" value="N/A" />
                                    </Col>
                                    <Col xs={12} sm={6} className="my-3">
                                        <Toggle
                                            className="toggle-button"
                                            checked={chronologicalLock}
                                            title="Chronology Lock"
                                            dataCy="library-training-module-chronologicalLock"
                                            leftLabel="Off"
                                            rightLabel="On"
                                            direction="vertical"
                                            onChange={(e) => setChronologicalLock(e)}
                                        />
                                    </Col>
                                    <Col xs={12} sm={6} className="my-3">
                                        <Toggle
                                            className="toggle-button"
                                            checked={timeLock}
                                            dataCy="library-training-module-timeLock"
                                            title="Time Lock"
                                            leftLabel="Off"
                                            rightLabel="On"
                                            direction="vertical"
                                            onChange={(e) => setTimeLock(e)}
                                        />
                                    </Col>
                                </>
                            )}
                        </Row>
                    </Accordion>

                    {type.label !== 'Quiz' && (
                        <div className="d-flex float-right mt-4">
                            <Button type="medium" dataCy="library-training-module-preview" name="Preview" busy={false} onClick={handlePreview} />
                            <Button className="ml-3" name="Draft" disabled={disableDraft} type="low" dataCy="library-training-module-saveDraft" onClick={handleSubmit(0)} />
                            <Button className="ml-3" name="Publish" disabled={disabled} dataCy="library-training-module-savePublish" onClick={handleSubmit(1)} />
                        </div>
                    )}
                </div>
            </div>

            {type.label === 'Quiz' && (
                <div className="library-module-card border-5" data-cy="library-training-module-questions">
                    <div className="detail-content">
                        <div className="border-bottom my-4">
                            <p className="dsl-b20 bold my-3">Questions</p>
                        </div>
                        {questions.map((item, index) => {
                            if (index < per * page && index >= per * (page - 1)) {
                                return (
                                    <Question
                                        key={index}
                                        index={index + 1}
                                        dataCy={`question${index}`}
                                        question={item}
                                        onAddAnswer={handleAddAnswer}
                                        onRemoveAnswer={handleRemoveAnswer}
                                        onChangeType={handleChangeQuizType}
                                        onChangeQuestion={handleChangeQuestion}
                                        onChangeAnswer={handleChangeAnswers}
                                        onChecked={handleChangeChecked}
                                        onRemoveQuestion={handleRemoveQuestion}
                                    />
                                );
                            }
                        })}
                        <div className="d-h-between mt-3">
                            <Pagination current={page} per={per} pers={[]} total={Math.ceil(questions.length / per)} onChange={(e) => setPage(e)} />
                            <Button dataCy="library-training-module-addQuestionBtn" className="ml-auto" type="medium" onClick={handleAddQuestion}>
                                <Icon name="fas fa-plus" size={14} color="#376caf" />
                                <span className="dsl-p14 text-400 ms-1">ADD QUESTION</span>
                            </Button>
                        </div>
                        <div className="d-flex float-right mt-4">
                            <Button className="ml-3" name="Draft" disabled={disableDraft} type="low" data-cy="library-training-module-saveDraftQuestionBtn" onClick={handleSubmit(0)} />
                            <Button className="ml-3" name="Publish" disabled={disabled} data-cy="library-training-module-savePublishQuestionBtn" onClick={handleSubmit(1)} />
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default Add;
